import { Record, OrderedSet } from 'immutable'
import { makeGraphQLReducer } from 'data/graphql/reducer/makeGraphQLReducer'

import {
  record as OrganizationRecord,
  recordPublicInfo as OrganizationPublicInfoRecord,
} from 'data/organizations'
import { record as ContactUserRecord } from 'data/contactUser'
import { record as UserRecord, UserConnectionRecord } from 'data/users'
import { record as UserPresenceRecord } from 'data/userPresence'
import { record as UserPreferencesRecord } from 'data/userPreferences'
import { record as NoteRecord } from 'data/notes'
import { record as PublicNoteRecord } from 'data/publicNotes'
import { record as NoteHistoryRecord } from 'data/notesHistory'
import { record as NoteActivityRecord } from 'data/noteActivities'
import { record as ApplicationRecord } from 'data/applications'
import { record as IntegrationRecord } from 'data/integrations'
import {
  record as ChannelRecord,
  recordChannelMembership as ChannelMembershipRecord,
} from 'data/channels'
import { record as NotificationRecord } from 'data/notifications'

import { record as CommentRecord } from 'data/comments'
import { record as ThreadRecord } from 'data/threads'
import { PlanRecord } from 'data/payment/plans'
import { PlanSubscriptionRecord } from 'data/payment/subscriptions'
import { PaymentInfosRecord } from 'data/payment/infos'
import { InvoiceRecord } from 'data/payment/invoices'
import { CouponRecord } from 'data/payment/coupons'
import { UserGroupRecord } from 'data/userGroups'

import {
  NoteAknowledgmentRecord,
  ChannelAknowledgmentRecord,
  ThreadAknowledgmentRecord,
} from 'data/activityRegistry/record'

const makeRecordMerger = Record => (oldRecord, newAttributes) => {
  return oldRecord ? oldRecord.merge(newAttributes) : new Record(newAttributes)
}

const UserPreferencesMerger = makeRecordMerger(UserPreferencesRecord)
const ChannelActivityAknowledgmentMerger = makeRecordMerger(
  ChannelAknowledgmentRecord
)
const NoteActivityAknowledgmentMerger = makeRecordMerger(
  NoteAknowledgmentRecord
)

const entityMergers = {
  Organization: makeRecordMerger(OrganizationRecord),
  OrganizationPublic: makeRecordMerger(OrganizationPublicInfoRecord),
  Notification: makeRecordMerger(NotificationRecord),

  User: makeRecordMerger(UserRecord),
  UserConnection: makeRecordMerger(UserConnectionRecord),
  UserGroup: makeRecordMerger(UserGroupRecord),
  ContactUser: makeRecordMerger(ContactUserRecord),
  UserPresence: makeRecordMerger(UserPresenceRecord),
  Application: makeRecordMerger(ApplicationRecord),
  Integration: makeRecordMerger(IntegrationRecord),

  UserPreferences: (oldRecord, newAttributes) =>
    UserPreferencesMerger(
      oldRecord,
      newAttributes
        .set(
          'favoriteChannelIds',
          OrderedSet(newAttributes.get('favoriteChannelIds'))
        )
        .set(
          'mutedChannelIds',
          OrderedSet(newAttributes.get('mutedChannelIds'))
        )
        .set(
          'bookmarkedNoteIds',
          OrderedSet(newAttributes.get('bookmarkedNoteIds'))
        )
        .set(
          'lastVisitedNoteIds',
          OrderedSet(newAttributes.get('lastVisitedNoteIds'))
        )
        .set(
          'lastVisitedChannelIds',
          OrderedSet(newAttributes.get('lastVisitedChannelIds'))
        )
    ),

  ChannelActivityAknowledgment: (oldRecord, newAttributes) =>
    ChannelActivityAknowledgmentMerger(
      oldRecord,
      newAttributes.set('id', newAttributes.get('entityId'))
    ),
  NoteActivityAknowledgment: (oldRecord, newAttributes) =>
    NoteActivityAknowledgmentMerger(
      oldRecord,
      newAttributes.set('id', newAttributes.get('entityId'))
    ),
  ThreadActivityAknowledgment: makeRecordMerger(ThreadAknowledgmentRecord),

  Channel: makeRecordMerger(ChannelRecord),
  ChannelMembership: makeRecordMerger(ChannelMembershipRecord),
  Thread: makeRecordMerger(ThreadRecord),
  Comment: makeRecordMerger(CommentRecord),
  Note: makeRecordMerger(NoteRecord),
  PublicNote: makeRecordMerger(PublicNoteRecord),
  NoteActivity: makeRecordMerger(NoteActivityRecord),
  NoteHistory: makeRecordMerger(NoteHistoryRecord),
  Plan: makeRecordMerger(PlanRecord),
  PlanSubscription: makeRecordMerger(PlanSubscriptionRecord),
  PaymentInfos: makeRecordMerger(PaymentInfosRecord),
  Invoice: makeRecordMerger(InvoiceRecord),
  Coupon: makeRecordMerger(CouponRecord),
}

const getEntityKeyField = record => {
  switch (record.get('__typename')) {
    case 'ChannelActivityAknowledgment':
    case 'ThreadActivityAknowledgment':
    case 'NoteActivityAknowledgment':
      return 'entityId'
    default:
      return 'id'
  }
}

const Reference = Record({
  id: null,
  __typename: null,
})
const refBuilder = ref => new Reference(ref)

const extraAddReducer = {
  ChannelMember: function addChannelReferences(
    state,
    { id, __typename, channel }
  ) {
    return state.updateIn(['Channel', channel.id, 'members'], members => {
      return members.set(id, refBuilder({ id, __typename }))
    })
  },
}

const extraRemoveReducer = {
  ChannelMember: function removeChannelReferences(state, { id, channel }) {
    return state.updateIn(['Channel', channel.id, 'members'], members =>
      members.remove(id)
    )
  },
}

export function makeReducer() {
  return makeGraphQLReducer({
    entityMergers,
    refBuilder,
    getEntityKeyField,
    extraAddReducer,
    extraRemoveReducer,
  })
}
